import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import AvatarGroup from '@mui/material/AvatarGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Autocomplete, Box, Fab, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { strings } from '../../constants/strings';
import { useDispatch, useSelector } from 'react-redux';
import { searchUser } from '../../redux/user/actions';
import _ from 'lodash';
import { IUserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { IHost, IPartner } from '../../redux/discovery/types';
import { createHost, createPartner, searchHost, searchPartner } from '../../redux/contest/actions';
import CustomButton from '../Button/CustomButton';
import { CustomTextField } from '../TextInput/CustomTextField';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { profile_pic } from '../../asset/images';
import { ISocials } from '../../redux/contest/types';
import {
    userSearchSelector,
    hostSearchSelector,
    partnerSearchSelector,
    searchingUserSearchSelector,
    searchingHostSelector,
    searchingPartnerSelector,
} from '../AvatarGroup/selector';
import { useStyles } from '../AvatarGroup/AvatarGroup.styles';
import LinkedinIcon from '../CustomSVGIcons/LinkedinIcon';
import FacebookIcon from '../CustomSVGIcons/FacebookIcon';
import TiktokIcon from '../CustomSVGIcons/TiktokIcon';
import XIcon from '../CustomSVGIcons/XIcon';
import ThspianIcon from '../CustomSVGIcons/ThspianIcon';
import InstagramIcon from '../CustomSVGIcons/InstagramIcon';
import SnapchatIcon from '../CustomSVGIcons/SnapchatIcon';

import { ContestHostPartnerPickerModal } from './ContestHostPartnerPickerModal';
import { ContestHostPartnerCreateModal } from './ContestHostPartnerCreateModal';

type AvatarSelectionCommon = {
    isEditMode?: boolean;
    title?: string;
    disableTitle?: boolean;
    placeholder?: string;
    /** Brand dashboard: full-screen curator picker with local recent history. */
    visualVariant?: 'default' | 'brand';
};

/** Props vary by `searchType` — host/partner were always passed at runtime but typed as jury-only. */
export type IAvatarSelection =
    | (AvatarSelectionCommon & {
          searchType?: 'jury';
          value?: IUserSummary[];
          onChange?: (selectedUsers: IUserSummary[] | undefined) => void;
      })
    | (AvatarSelectionCommon & {
          searchType: 'host';
          value?: IHost;
          onChange?: (selected: IHost | undefined) => void;
      })
    | (AvatarSelectionCommon & {
          searchType: 'partner';
          value?: IPartner[];
          onChange?: (selected: IPartner[] | undefined) => void;
      });

interface IFormState {
    name: string;
    description: string;
    established: string;
    website: string;
    created_by: string;
    profilePicture: File | null;
    socials: ISocials;
}

/**
 *
 * @param param0
 * @returns
 *
 * I will attempt to handle the api call and redux operation for this module inside here
 * As I see that it only has one functionality, which is to search and select users.
 * In the future we will move it out of here to the parent to keep it uniform.
 */
const PLATFORMS: string[] = ['thspian', 'tik_tok', 'facebook', 'instagram', 'x', 'snapchat', 'linkedin'];
const inputProps = {
    style: { color: 'white', border: '1px solid grey', borderRadius: 10 },
};
const UploadContestAvatarGroup: React.FC<IAvatarSelection> = ({
    isEditMode = true,
    disableTitle,
    title,
    placeholder,
    value,
    onChange,
    searchType,
    visualVariant = 'default',
}) => {
    const [selectedAvatar, setSelectedAvatar] = useState<IUserSummary[]>(
        searchType === 'host' || searchType === 'partner' ? [] : Array.isArray(value) ? value : [],
    );
    const [selectedHost, setSelectedHost] = useState<IHost | undefined>(
        searchType === 'host' && value && !Array.isArray(value) ? value : undefined,
    );
    const [selectedPartners, setSelectedPartners] = useState<IPartner[]>(
        searchType === 'partner' && Array.isArray(value) ? value : [],
    );
    const [openDialog, setOpenDialog] = useState(false);
    const [socialsModalOpen, setSocialsModalOpen] = useState(false);
    const [, setSearchValue] = useState('');
    const [profilePicture, setProfilePicture] = useState<string | undefined>();
    const [isSearchModalOpen, setSearchModalOpen] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [link, setLink] = useState('');
    const [isProfilePictureEditMode, setIsProfilePictureEditMode] = useState(false);

    const [formState, setFormState] = useState<IFormState>({
        name: '',
        description: '',
        established: '',
        website: '',
        profilePicture: null,
        created_by: '',
        socials: {
            instagram: null,
            x: null,
            thspian: null,
            tik_tok: null,
            snapchat: null,
            linkedin: null,
            facebook: null,
        },
    });

    const { themeStyle, device } = useThemeContext();
    const classes = useStyles();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const hostResults = useSelector(hostSearchSelector);
    const partnerResult = useSelector(partnerSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);
    const searchingHost = useSelector(searchingHostSelector);
    const searchingPartner = useSelector(searchingPartnerSelector);

    useEffect(() => {
        switch (searchType) {
            case 'host':
                setSelectedHost(value && !Array.isArray(value) ? value : undefined);
                break;
            case 'partner':
                setSelectedPartners(Array.isArray(value) ? value : []);
                break;
            default:
                setSelectedAvatar(Array.isArray(value) ? value : []);
                break;
        }
    }, [value, searchType]);

    const emitChange = useCallback(
        (payload: IUserSummary[] | IHost | IPartner[] | undefined) => {
            if (!onChange) return;
            switch (searchType) {
                case 'host':
                    (onChange as (selected: IHost | undefined) => void)(payload as IHost | undefined);
                    break;
                case 'partner':
                    (onChange as (selected: IPartner[] | undefined) => void)(payload as IPartner[] | undefined);
                    break;
                default:
                    (onChange as (selected: IUserSummary[] | undefined) => void)(payload as IUserSummary[] | undefined);
                    break;
            }
        },
        [onChange, searchType],
    );

    const handleAvatarClick = (avatar: IUserSummary) => {
        const filteredAvatar = selectedAvatar.filter(a => a._id !== avatar._id);
        setSelectedAvatar(filteredAvatar);
        emitChange(filteredAvatar);
    };

    const handleHostClick = (avatar: IHost) => {
        setSelectedHost(undefined);
        emitChange(undefined);
    };

    const handlePartnerClick = (avatar: IPartner) => {
        const filteredAvatar = selectedPartners.filter(a => a._id !== avatar._id);
        setSelectedPartners(filteredAvatar);
        emitChange(filteredAvatar);
    };

    const handleAddAvatarClick = () => {
        setSearchModalOpen(true);
    };

    const handleModalClose = () => {
        setSearchModalOpen(false);
    };

    const handleAvatarSelect = (avatar: IUserSummary) => {
        const userSummary: IUserSummary = {
            _id: avatar._id,
            firstName: avatar.firstName,
            lastName: avatar.lastName,
            profile_picture: avatar.profile_picture || '',
            userName: avatar.userName,
            role: '',
            audme_profile: '',
            id: avatar._id,
        };

        if (selectedAvatar.some(a => a._id === avatar._id)) {
            handleAvatarClick(userSummary);
        } else {
            setSearchModalOpen(false);
            const previosAvatar = [...selectedAvatar, userSummary];
            setSelectedAvatar(previosAvatar);
            emitChange([...selectedAvatar, userSummary]);
        }
    };

    const handleHostSelect = (host: IHost) => {
        if (selectedHost !== undefined) {
            handleHostClick(host);
        } else {
            setSearchModalOpen(false);
            setSelectedHost(host);
            emitChange(host);
        }
    };

    const handlePartnerSelect = (partner: IPartner) => {
        if (selectedPartners && selectedPartners.includes(partner)) {
            handlePartnerClick(partner);
        } else {
            setSearchModalOpen(false);
            const previosAvatar = [...selectedPartners, partner];
            setSelectedPartners(previosAvatar);
            emitChange([...selectedPartners, partner]);
        }
    };

    const handleSearch = useRef(
        _.debounce(searchTerm => {
            if (searchTerm.length > 3) {
                switch (searchType) {
                    case 'host':
                        !searchingHost && dispatch(searchHost({ searchTerm }));
                        break;
                    case 'jury':
                        !searchingUser && dispatch(searchUser({ searchTerm }));
                        break;
                    case 'partner':
                        !searchingPartner && dispatch(searchPartner({ searchTerm }));
                        break;
                }
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: { target: { value: React.SetStateAction<string> } }) => {
        setSearchValue(event.target.value);
        handleSearch(event.target.value);
    };
    const handleDialogClose = () => {
        setOpenDialog(false);
    };
    const handleDialogOpen = () => {
        setOpenDialog(true);
    };

    ///Handle social media save
    const handleOnSave = () => {
        handleSocialsModalClose();
        if (selectedPlatform && link) {
            setFormState(prevState => ({
                ...prevState,
                socials: { ...prevState.socials, [selectedPlatform]: link },
            }));
            setSelectedPlatform('');
            setLink('');
        }
    };

    const handleOnHostSave = () => {
        const action = searchType && searchType === 'host' ? createHost : createPartner;

        dispatch(
            action({
                name: formState.name,
                description: formState.description,
                established: formState.established,
                website: formState.website,
                socials: formState.socials,
                profile_picture: formState.profilePicture ?? undefined,
            }),
        );
        handleDialogClose();
    };

    const handleAddSocials = () => {
        setSocialsModalOpen(true);
    };
    const handleSocialsModalClose = () => {
        setSocialsModalOpen(false);
    };

    const SocialMediaIcon = ({ platform }: { platform: string }) => {
        switch (platform) {
            case 'facebook':
                return <FacebookIcon fill={theme[themeStyle].lightWhite} />;
            case 'tik_tok':
                return <TiktokIcon stroke={theme[themeStyle].lightWhite} />;
            case 'x':
                return <XIcon fill={theme[themeStyle].lightWhite} />;
            case 'thspian':
                return <ThspianIcon fill={theme[themeStyle].lightWhite} />;
            case 'instagram':
                return <InstagramIcon fill={theme[themeStyle].lightWhite} />;
            case 'snapchat':
                return <SnapchatIcon stroke={theme[themeStyle].lightWhite} />;
            case 'linkedin':
                return <LinkedinIcon fill={theme[themeStyle].lightWhite} />;
            default:
                return <ThspianIcon fill={theme[themeStyle].lightWhite} />;
        }
    };
    const isAddSocialsButtonEnabled = () => {
        return !link || !selectedPlatform;
    };
    const isCreateAHostButtonEnabled = () => {
        return (
            !formState.name ||
            !formState.description ||
            !formState.established ||
            !formState.website ||
            !formState.profilePicture ||
            !formState.socials
        );
    };

    const handleOnChange = (field: string, value: string) => {
        setFormState(prevState => ({ ...prevState, [field]: value }));
    };
    const handleProfilePictureChange = (imageUrl: string | undefined, file: File) => {
        console.log(file);
        setFormState(prevState => ({
            ...prevState,
            profilePicture: file,
        }));
        setProfilePicture(imageUrl);
    };
    const handleOnClick = () => {
        setIsProfilePictureEditMode(!isProfilePictureEditMode);
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target && e.target.files) {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();

                reader.onload = () => {
                    // Call handleProfilePictureChange to update profilePicture in formState
                    handleProfilePictureChange(reader.result as string, file);
                };
                reader.readAsDataURL(file);
            }
        }
    };
    return (
        <>
            {!disableTitle && (
                <Typography
                    color="gray"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 16,
                        marginTop: 2,
                        marginBottom: 1,
                    }}>
                    {title ?? strings.select_judges}
                </Typography>
            )}
            {(searchType === 'host' || searchType === 'partner') && visualVariant === 'brand' ? (
                <ContestHostPartnerPickerModal
                    open={isSearchModalOpen}
                    onClose={handleModalClose}
                    searchType={searchType}
                    value={searchType === 'host' ? selectedHost : selectedPartners}
                    placeholder={placeholder}
                    onCreateNew={() => {
                        setSearchModalOpen(false);
                        setOpenDialog(true);
                    }}
                    onConfirm={confirmed => {
                        if (searchType === 'host') {
                            const host = confirmed as IHost | undefined;
                            setSelectedHost(host);
                            emitChange(host);
                        } else {
                            const partners = (confirmed as IPartner[] | undefined) ?? [];
                            setSelectedPartners(partners);
                            emitChange(partners);
                        }
                    }}
                />
            ) : (
                <Dialog
                    open={isSearchModalOpen}
                    onClose={handleModalClose}
                    aria-hidden={!isSearchModalOpen ? 'true' : 'false'}
                    color="blue"
                    PaperProps={{
                        style: {
                            backgroundColor: theme[themeStyle].pure_black,
                            height: '50%',
                            width: device.isDesktop ? '30%' : '80%',
                            overflowY: 'scroll',
                            border: '1px solid grey',
                            borderRadius: 10,
                            backdropFilter: 'blur(20px)',
                        },
                    }}>
                    <TextField
                        autoFocus
                        variant="outlined"
                        placeholder={placeholder ?? `Search and select a ${searchType}...`}
                        color="secondary"
                        InputProps={{
                            placeholder: placeholder ?? `Search and select a ${searchType}...`,
                            style: {
                                color: theme[themeStyle].lightWhite,
                                border: `1px solid ${theme[themeStyle].lightWhite}`,
                                borderRadius: '50px',
                                paddingLeft: '8px',
                                fontSize: 12,
                                minWidth: 200,
                            },
                        }}
                        inputProps={{
                            color: 'blue',
                        }}
                        onChange={e => {
                            handleSearchInputChange(e);
                        }}
                        sx={{
                            padding: 3,
                        }}
                    />
                    <DialogContent>
                        {!!userResults &&
                            searchType === 'jury' &&
                            !_.isEmpty(userResults) &&
                            userResults.map((avatar, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}>
                                    <Stack flexDirection="row" alignItems="center" mb={1.2} gap={0.5}>
                                        <ThspianAvatar
                                            user={avatar as any}
                                            onClick={() => handleAvatarSelect(avatar)}
                                            sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <Typography sx={{ color: theme[themeStyle].lightWhite }}>
                                            {avatar.firstName} {avatar.lastName}
                                        </Typography>
                                    </Stack>

                                    {selectedAvatar.some(a => a._id === avatar._id) && (
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleAvatarSelect(avatar)}
                                            style={{
                                                borderRadius: '50%',
                                                borderWidth: 1,
                                                borderColor: theme[themeStyle].lightWhite,
                                            }}>
                                            <CheckCircleIcon sx={{ color: theme[themeStyle].highlight }} />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                        {!!hostResults &&
                            searchType === 'host' &&
                            !_.isEmpty(hostResults) &&
                            hostResults.map((avatar, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}>
                                    <Stack flexDirection="row" alignItems="center" mb={1.2} gap={0.5}>
                                        <ThspianAvatar
                                            user={avatar as unknown as IUserSummary}
                                            onClick={() => handleHostSelect(avatar)}
                                            sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <Typography sx={{ color: theme[themeStyle].lightWhite }}>
                                            {avatar.name}
                                        </Typography>
                                    </Stack>

                                    {selectedHost?._id === avatar._id && (
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleHostSelect(avatar)}
                                            style={{
                                                borderRadius: '50%',
                                                borderWidth: 1,
                                                borderColor: theme[themeStyle].lightWhite,
                                            }}>
                                            <CheckCircleIcon sx={{ color: theme[themeStyle].highlight }} />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                        {!!partnerResult &&
                            searchType === 'partner' &&
                            !_.isEmpty(partnerResult) &&
                            partnerResult.map((avatar, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}>
                                    <Stack flexDirection="row" alignItems="center" mb={1.2} gap={0.5}>
                                        <ThspianAvatar
                                            user={avatar as unknown as IUserSummary}
                                            onClick={() => handlePartnerSelect(avatar)}
                                            sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <Typography sx={{ color: theme[themeStyle].lightWhite }}>
                                            {avatar.name}
                                        </Typography>
                                    </Stack>

                                    {selectedPartners && selectedPartners.includes(avatar) && (
                                        <IconButton
                                            color="primary"
                                            onClick={() => handlePartnerSelect(avatar)}
                                            style={{
                                                borderRadius: '50%',
                                                borderWidth: 1,
                                                borderColor: theme[themeStyle].lightWhite,
                                            }}>
                                            <CheckCircleIcon sx={{ color: theme[themeStyle].highlight }} />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                    </DialogContent>
                    <DialogActions sx={{ position: 'absolute', right: 0, top: 0 }}>
                        <IconButton
                            color="primary"
                            onClick={handleModalClose}
                            style={{
                                borderRadius: '50%',
                                borderWidth: 1,
                                borderColor: theme[themeStyle].lightWhite,
                                width: 30,
                            }}>
                            <CloseIcon stroke={theme[themeStyle].lightWhite} strokeWidth={4} />
                        </IconButton>
                    </DialogActions>
                    {searchType && searchType !== 'jury' && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 0.5,
                                margin: 1,
                            }}>
                            <Box>
                                <Fab size="small" onClick={handleDialogOpen}>
                                    <AddIcon />
                                </Fab>
                            </Box>
                        </Box>
                    )}
                </Dialog>
            )}

            {(searchType === 'host' || searchType === 'partner') && visualVariant === 'brand' ? (
                <ContestHostPartnerCreateModal
                    open={openDialog}
                    onClose={handleDialogClose}
                    searchType={searchType}
                    formState={formState}
                    profilePreview={profilePicture}
                    onFieldChange={handleOnChange}
                    onImageChange={handleImageChange}
                    onAddSocials={handleAddSocials}
                    onSocialRemove={platform =>
                        setFormState(prev => ({
                            ...prev,
                            socials: { ...prev.socials, [platform]: null },
                        }))
                    }
                    onSave={handleOnHostSave}
                    saveDisabled={isCreateAHostButtonEnabled()}
                />
            ) : (
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    aria-hidden={!openDialog ? 'true' : 'false'}
                    PaperProps={{
                        style: {
                            backgroundColor: theme[themeStyle].pure_black,
                            height: '50%',
                            width: device.isDesktop ? '30%' : '80%',
                            overflowY: 'scroll',
                            border: '1px solid grey',
                            borderRadius: 10,
                            backdropFilter: 'blur(20px)',
                        },
                    }}>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].lightWhite,
                                    padding: 1,
                                    textTransform: 'capitalize',
                                    fontSize: 18,
                                }}>{`Create a ${searchType}`}</Typography>
                        </Box>

                        {/* Name Field */}
                        <Box sx={{ paddingBottom: 4, margin: 2 }}>
                            <CustomTextField
                                label="Name *"
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                        value: formState['name'],
                                        onChange: e => handleOnChange('name', e.target.value),
                                    },
                                }}
                            />

                            {/* Description Field */}
                            <Box sx={{ paddingBottom: 1 }}>
                                <CustomTextField
                                    label="Description"
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            placeholder: `Tell us about this ${searchType}...`,
                                        },
                                        multiline: true,
                                        minRows: 2,
                                        maxRows: 3,
                                        value: formState['description'],
                                        onChange: e => handleOnChange('description', e.target.value),
                                    }}
                                />
                            </Box>

                            {/* Established Field */}
                            <Box sx={{ paddingBottom: 2 }}>
                                <CustomTextField
                                    label="Established"
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            value: formState['established'],
                                            onChange: e => handleOnChange('established', e.target.value),
                                        },
                                    }}
                                />
                            </Box>
                            {/* Website Field */}
                            <Box sx={{ paddingBottom: 2 }}>
                                <CustomTextField
                                    label="Website"
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            value: formState['website'],
                                            onChange: e => handleOnChange('website', e.target.value),
                                        },
                                    }}
                                />
                            </Box>

                            {/* Add Profile Picture*/}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    marginBottom: 2,
                                    gap: 1,
                                }}>
                                <Box
                                    onClick={handleOnClick}
                                    sx={{
                                        borderRadius: '50%',
                                        position: 'relative',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                    }}>
                                    {formState.profilePicture ? (
                                        <>
                                            <CustomLazyLoadImage
                                                src={profilePicture}
                                                alt={'Add profile picture'}
                                                style={{
                                                    borderRadius: '50%',
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                            <input
                                                accept="image/*"
                                                id="icon-contest-image"
                                                type="file"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    opacity: 0,
                                                    cursor: 'pointer',
                                                    borderRadius: '50%',
                                                }}
                                                onChange={handleImageChange}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <CustomLazyLoadImage
                                                src={profile_pic}
                                                alt="add cover icon"
                                                style={{
                                                    borderRadius: '50%',
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                            <input
                                                accept="image/*"
                                                id="icon-contest-image"
                                                type="file"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    opacity: 0,
                                                    cursor: 'pointer',
                                                    borderRadius: '50%',
                                                }}
                                                onChange={handleImageChange}
                                            />
                                        </>
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                        color: theme[themeStyle].lightWhite,
                                    }}>
                                    Add Profile Picture
                                </Typography>
                            </Box>

                            {/* Add Socials button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    flexDirection: 'row-reverse',
                                    gap: 1,
                                    marginBottom: 2,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                        color: theme[themeStyle].lightWhite,
                                    }}>
                                    Add Socials
                                </Typography>
                                <Fab size="small" onClick={handleAddSocials}>
                                    <AddIcon />
                                </Fab>
                            </Box>

                            {
                                <Box sx={{ marginTop: 2, display: 'flex', gap: 1 }}>
                                    {Object.entries(formState.socials).map(
                                        ([platform, link], index) =>
                                            link && (
                                                <a
                                                    key={index}
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ textDecoration: 'none' }}>
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: 1,
                                                            height: { xs: 20, sm: 30 },
                                                            width: { xs: 20, sm: 30 },
                                                            overflow: 'hidden',
                                                        }}>
                                                        <SocialMediaIcon platform={platform} />
                                                    </Box>
                                                </a>
                                            ),
                                    )}
                                </Box>
                            }

                            {/* Create a Partner/Host Save Button */}
                            <CustomButton
                                label="Save"
                                onClick={() => handleOnHostSave()}
                                disabled={isCreateAHostButtonEnabled()}
                                sx={{
                                    textDecoration: 'none',
                                    textTransform: 'none',
                                    width: '100%',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                    '&:hover': {
                                        border: `2px solid ${theme[themeStyle].primary_orange}`,
                                        backgroundColor: theme[themeStyle].pure_black,
                                        color: theme[themeStyle].primary_orange,
                                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                                    },
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ position: 'absolute', right: 0, top: 0 }}>
                        <IconButton
                            color="primary"
                            onClick={handleDialogClose}
                            style={{
                                borderRadius: '50%',
                                borderWidth: 1,
                                borderColor: theme[themeStyle].lightWhite,
                                width: 30,
                            }}>
                            <CloseIcon stroke={theme[themeStyle].lightWhite} strokeWidth={4} />
                        </IconButton>
                    </DialogActions>
                </Dialog>
            )}

            {/* { Add Socials Modal} */}
            {
                <Dialog
                    open={socialsModalOpen}
                    onClose={handleSocialsModalClose}
                    aria-hidden={!socialsModalOpen ? 'true' : 'false'}
                    PaperProps={{
                        style: {
                            backgroundColor: theme[themeStyle].pure_black,
                            height: '50%',
                            width: device.isDesktop ? '30%' : '80%',
                            overflowY: 'scroll',
                            border: '1px solid grey',
                            borderRadius: 10,
                            backdropFilter: 'blur(20px)',
                        },
                    }}>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].lightWhite,
                                    padding: 1,
                                    textTransform: 'capitalize',
                                    fontSize: 18,
                                }}>
                                Add Socials
                            </Typography>
                        </Box>

                        {/* Select Platform */}
                        <Box sx={{ paddingBottom: 4, margin: 2 }}>
                            <FormLabel
                                sx={{
                                    textAlign: 'left',
                                    fontSize: 14,
                                    fontStyle: 'normal',
                                    fontWeight: '400',
                                    color: theme[themeStyle].form_label,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                }}>
                                Social Media Platform
                            </FormLabel>

                            <Autocomplete
                                options={PLATFORMS}
                                getOptionLabel={(platform: string) =>
                                    platform.replace('_', '').replace(/\b\w/g, char => char.toUpperCase())
                                }
                                sx={{
                                    width: '100%',
                                    color: theme[themeStyle].lightWhite,
                                    marginTop: 1,
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            ...inputProps,
                                            style: { color: theme[themeStyle].lightWhite, borderRadius: 10 },
                                        }}
                                    />
                                )}
                                onChange={(event, newValue) => setSelectedPlatform(newValue ?? '')}
                                style={{
                                    backgroundColor: theme[themeStyle].form_gray,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: 10,
                                    color: theme[themeStyle].lightWhite,
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    return option === value;
                                }}
                                ListboxProps={{
                                    sx: {
                                        backgroundColor: theme[themeStyle].form_gray,
                                        color: theme[themeStyle].lightWhite,
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                    },
                                }}
                                clearIcon={<CloseIcon stroke={theme[themeStyle].lightWhite} />}
                            />

                            {/* Enter Link */}
                            <Box sx={{ paddingBottom: 2 }}>
                                <CustomTextField
                                    label="Enter Link"
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            value: link,
                                        },
                                    }}
                                    onChange={e => {
                                        setLink(e.target.value);
                                    }}
                                />
                            </Box>

                            {/* Add Socials Save button */}
                            <CustomButton
                                label="Save"
                                onClick={handleOnSave}
                                disabled={isAddSocialsButtonEnabled()}
                                sx={{
                                    textDecoration: 'none',
                                    textTransform: 'none',
                                    width: '100%',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                    '&:hover': {
                                        border: `2px solid ${theme[themeStyle].primary_orange}`,
                                        backgroundColor: theme[themeStyle].pure_black,
                                        color: theme[themeStyle].primary_orange,
                                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                                    },
                                }}></CustomButton>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ position: 'absolute', right: 0, top: 0 }}>
                        <IconButton
                            color="primary"
                            onClick={handleSocialsModalClose}
                            style={{
                                borderRadius: '50%',
                                borderWidth: 1,
                                borderColor: theme[themeStyle].lightWhite,
                                width: 30,
                            }}>
                            <CloseIcon stroke={theme[themeStyle].lightWhite} strokeWidth={4} />
                        </IconButton>
                    </DialogActions>
                </Dialog>
            }

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    ml: visualVariant === 'brand' ? 0 : 1,
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                <AvatarGroup max={3} variant="circular" className={classes.customAvatarGroup}>
                    {selectedAvatar &&
                        searchType === 'jury' &&
                        selectedAvatar.map((avatar, index) => (
                            <ThspianAvatar user={avatar as any} key={index} onClick={() => handleAvatarClick(avatar)} />
                        ))}
                    {selectedPartners &&
                        searchType === 'partner' &&
                        selectedPartners.map((avatar, index) => (
                            <ThspianAvatar
                                user={avatar as unknown as IUserSummary}
                                key={index}
                                onClick={() => handlePartnerClick(avatar)}
                            />
                        ))}
                    {selectedHost !== undefined && searchType === 'host' && (
                        <ThspianAvatar
                            user={selectedHost as unknown as IUserSummary}
                            onClick={() => handleHostClick(selectedHost)}
                        />
                    )}
                </AvatarGroup>
                {(selectedHost !== undefined && searchType === 'host') ||
                    (isEditMode && (
                        <IconButton
                            color="primary"
                            onClick={handleAddAvatarClick}
                            style={{
                                borderRadius: visualVariant === 'brand' ? 12 : '50%',
                                border: `1px solid ${
                                    visualVariant === 'brand' ? 'rgba(255,201,101,0.35)' : theme[themeStyle].lightWhite
                                }`,
                                marginLeft: visualVariant === 'brand' ? 0 : 8,
                                backgroundColor: visualVariant === 'brand' ? 'rgba(255,201,101,0.08)' : undefined,
                            }}>
                            <AddIcon
                                style={{
                                    height: 20,
                                    width: 20,
                                    color: visualVariant === 'brand' ? '#ffc965' : theme[themeStyle].primary_orange,
                                }}
                            />
                        </IconButton>
                    ))}
            </Box>
        </>
    );
};

export { UploadContestAvatarGroup };
